import React, { useState, useEffect } from 'react';
import api from '../services/api';

const PopulationStructure = ({ crop }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // PCA state
  const [nComponents, setNComponents] = useState(3);
  const [minMaf, setMinMaf] = useState(0.01);  // More permissive default
  const [maxMissing, setMaxMissing] = useState(0.5);  // More permissive default
  const [pcaResults, setPcaResults] = useState(null);
  const [pcaLoading, setPcaLoading] = useState(false);
  const [radFilter, setRadFilter] = useState(null);  // '2', '6', or null
  
  // Clustering state
  const [nClusters, setNClusters] = useState(3);
  const [usePca, setUsePca] = useState(true);
  const [clusterResults, setClusterResults] = useState(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  
  // Visualization state
  const [pcXAxis, setPcXAxis] = useState(0); // PC1
  const [pcYAxis, setPcYAxis] = useState(1); // PC2
  const [colorBy, setColorBy] = useState('none'); // 'none' or 'cluster'

  useEffect(() => {
    fetchTables();
  }, [crop]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      setError('');
      let url = '/api/population/tables';
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await api.get(url);
      setTables(response.data.tables);
    } catch (err) {
      setError('Failed to fetch tables: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRunPCA = async () => {
    if (!selectedTable) {
      setError('Please select a table');
      return;
    }

    try {
      setPcaLoading(true);
      setError('');
      
      const response = await api.post('/api/population/pca', {
        genotype_table: selectedTable,
        n_components: nComponents,
        min_maf: minMaf,
        max_missing: maxMissing,
        rad_filter: radFilter
      });

      setPcaResults(response.data);
    } catch (err) {
      setError('PCA analysis failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPcaLoading(false);
    }
  };

  const handleRunClustering = async () => {
    if (!selectedTable) {
      setError('Please select a table');
      return;
    }

    try {
      setClusterLoading(true);
      setError('');
      
      const response = await api.post('/api/population/cluster', {
        genotype_table: selectedTable,
        n_clusters: nClusters,
        use_pca: usePca,
        n_pca_components: 10,
        rad_filter: radFilter
      });

      setClusterResults(response.data);
      setColorBy('cluster');
    } catch (err) {
      setError('Clustering failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setClusterLoading(false);
    }
  };

  const downloadPCAData = () => {
    if (!pcaResults) return;
    
    // Create CSV content
    const headers = ['Sample_ID', ...Array.from({length: nComponents}, (_, i) => `PC${i+1}`)];
    const rows = Object.entries(pcaResults.components).map(([sample_id, components]) => {
      return [sample_id, ...components].join(',');
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    
    // Download file
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pca_results_${selectedTable}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadClusterData = () => {
    if (!clusterResults) return;
    
    // Create CSV content
    const headers = ['Sample_ID', 'Cluster'];
    const rows = Object.entries(clusterResults.cluster_assignments).map(([sample_id, cluster]) => {
      return `${sample_id},${cluster}`;
    });
    
    const csv = [headers.join(','), ...rows].join('\n');
    
    // Download file
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cluster_results_${selectedTable}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadPCAPlotImage = () => {
    const svgElement = document.getElementById('pca-plot-svg');
    if (!svgElement) return;

    // Create canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Get SVG dimensions
    const svgRect = svgElement.getBoundingClientRect();
    canvas.width = svgRect.width * 2; // 2x for better quality
    canvas.height = svgRect.height * 2;
    
    // Scale for better quality
    ctx.scale(2, 2);
    
    // Serialize SVG to string
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    // Load SVG into image
    const img = new Image();
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width / 2, canvas.height / 2);
      
      // Draw image
      ctx.drawImage(img, 0, 0);
      
      // Convert to PNG and download
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `pca_plot_${selectedTable}_PC${pcXAxis+1}_PC${pcYAxis+1}.png`;
        a.click();
        URL.revokeObjectURL(pngUrl);
        URL.revokeObjectURL(url);
      });
    };
    img.src = url;
  };

  const downloadVarianceChartImage = () => {
    const chartElement = document.getElementById('variance-chart-container');
    if (!chartElement) return;

    // Use html2canvas library alternative - manual canvas drawing
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 700;
    canvas.height = 400;
    
    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Title
    ctx.fillStyle = '#333';
    ctx.font = 'bold 18px Arial';
    ctx.fillText('Variance Explained by Each PC', 20, 30);
    
    // Draw bars
    const maxVariance = Math.max(...pcaResults.variance_explained);
    const barHeight = 30;
    const barSpacing = 10;
    const startY = 60;
    const maxBarWidth = 550;
    
    pcaResults.variance_explained.forEach((variance, idx) => {
      const y = startY + (barHeight + barSpacing) * idx;
      const barWidth = (variance / maxVariance) * maxBarWidth;
      
      // Label
      ctx.fillStyle = '#333';
      ctx.font = '14px Arial';
      ctx.fillText(`PC${idx + 1}`, 20, y + barHeight / 2 + 5);
      
      // Bar background
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(80, y, maxBarWidth, barHeight);
      
      // Bar fill
      ctx.fillStyle = '#025c4f';
      ctx.fillRect(80, y, barWidth, barHeight);
      
      // Percentage text
      ctx.fillStyle = 'white';
      ctx.font = 'bold 12px Arial';
      ctx.fillText(`${variance.toFixed(2)}%`, 85, y + barHeight / 2 + 5);
    });
    
    // Cumulative variance
    const cumY = startY + (barHeight + barSpacing) * pcaResults.variance_explained.length + 20;
    ctx.fillStyle = '#333';
    ctx.font = 'bold 14px Arial';
    ctx.fillText(
      `Cumulative Variance (PC1-PC${nComponents}): ${pcaResults.cumulative_variance[nComponents-1].toFixed(2)}%`,
      20,
      cumY
    );
    
    // Download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `variance_explained_${selectedTable}.png`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const renderPCAPlot = () => {
    if (!pcaResults) return null;

    const samples = Object.keys(pcaResults.components);
    const pcX = samples.map(s => pcaResults.components[s][pcXAxis]);
    const pcY = samples.map(s => pcaResults.components[s][pcYAxis]);

    // Calculate plot dimensions
    const xMin = Math.min(...pcX);
    const xMax = Math.max(...pcX);
    const yMin = Math.min(...pcY);
    const yMax = Math.max(...pcY);
    
    const xRange = xMax - xMin;
    const yRange = yMax - yMin;
    const padding = 0.1;
    
    const plotWidth = 700;
    const plotHeight = 500;
    
    // Scale function
    const scaleX = (val) => ((val - xMin) / xRange) * (plotWidth - 100) + 50;
    const scaleY = (val) => plotHeight - 50 - ((val - yMin) / yRange) * (plotHeight - 100);

    return (
      <div style={styles.plotContainer}>
        <div style={styles.plotHeader}>
          <h4>PCA Plot</h4>
          <div style={styles.axisSelectors}>
            <label>
              X-axis: 
              <select value={pcXAxis} onChange={(e) => setPcXAxis(parseInt(e.target.value))} style={styles.axisSelect}>
                {Array.from({length: nComponents}, (_, i) => (
                  <option key={i} value={i}>PC{i+1} ({pcaResults.variance_explained[i].toFixed(1)}%)</option>
                ))}
              </select>
            </label>
            <label style={{marginLeft: '20px'}}>
              Y-axis: 
              <select value={pcYAxis} onChange={(e) => setPcYAxis(parseInt(e.target.value))} style={styles.axisSelect}>
                {Array.from({length: nComponents}, (_, i) => (
                  <option key={i} value={i}>PC{i+1} ({pcaResults.variance_explained[i].toFixed(1)}%)</option>
                ))}
              </select>
            </label>
            <button onClick={downloadPCAPlotImage} style={{...styles.downloadButton, marginLeft: '20px'}}>
              📥 Download Plot (PNG)
            </button>
          </div>
        </div>
        
        <svg id="pca-plot-svg" width={plotWidth} height={plotHeight} style={styles.svg}>
          {/* Grid lines */}
          <line x1={50} y1={plotHeight-50} x2={plotWidth-50} y2={plotHeight-50} stroke="#ccc" strokeWidth={2} />
          <line x1={50} y1={50} x2={50} y2={plotHeight-50} stroke="#ccc" strokeWidth={2} />
          
          {/* Axis labels */}
          <text x={plotWidth/2} y={plotHeight-10} textAnchor="middle" style={styles.axisLabel}>
            PC{pcXAxis+1} ({pcaResults.variance_explained[pcXAxis].toFixed(1)}%)
          </text>
          <text x={15} y={plotHeight/2} textAnchor="middle" transform={`rotate(-90 15 ${plotHeight/2})`} style={styles.axisLabel}>
            PC{pcYAxis+1} ({pcaResults.variance_explained[pcYAxis].toFixed(1)}%)
          </text>
          
          {/* Data points */}
          {samples.map((sample, idx) => {
            const x = scaleX(pcX[idx]);
            const y = scaleY(pcY[idx]);
            const clusterColor = colorBy === 'cluster' && clusterResults 
              ? getClusterColor(clusterResults.cluster_assignments[sample])
              : '#025c4f';
            
            return (
              <g key={sample}>
                <circle
                  cx={x}
                  cy={y}
                  r={5}
                  fill={clusterColor}
                  opacity={0.7}
                  stroke="#fff"
                  strokeWidth={1}
                />
                <title>{sample}</title>
              </g>
            );
          })}
        </svg>
        
        {colorBy === 'cluster' && clusterResults && (
          <div style={styles.legend}>
            <strong>Clusters:</strong>
            {Array.from({length: nClusters}, (_, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{...styles.legendColor, backgroundColor: getClusterColor(i)}}></div>
                Cluster {i + 1}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getClusterColor = (clusterId) => {
    const colors = ['#025c4f', '#ff6b6b', '#4ecdc4', '#ffe66d', '#a8dadc', '#f1faee'];
    return colors[clusterId % colors.length];
  };

  const renderVarianceChart = () => {
    if (!pcaResults) return null;

    const maxVariance = Math.max(...pcaResults.variance_explained);
    const barHeight = 30;
    const chartWidth = 600;

    return (
      <div id="variance-chart-container" style={styles.varianceChart}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4 style={{ margin: 0 }}>Variance Explained by Each PC</h4>
          <button onClick={downloadVarianceChartImage} style={styles.downloadButton}>
            📥 Download Chart (PNG)
          </button>
        </div>
        {pcaResults.variance_explained.map((variance, idx) => (
          <div key={idx} style={styles.varianceBar}>
            <div style={styles.varianceLabel}>PC{idx + 1}</div>
            <div style={styles.varianceBarContainer}>
              <div 
                style={{
                  ...styles.varianceBarFill,
                  width: `${(variance / maxVariance) * 100}%`
                }}
              >
                <span style={styles.varianceText}>{variance.toFixed(2)}%</span>
              </div>
            </div>
          </div>
        ))}
        <div style={styles.cumulativeVariance}>
          <strong>Cumulative Variance (PC1-PC{nComponents}):</strong> {pcaResults.cumulative_variance[nComponents-1].toFixed(2)}%
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>🧬 Population Structure & PCA</h2>
      
      <div style={styles.infoBox}>
        <strong>About:</strong> Analyze population structure using Principal Component Analysis (PCA) and clustering.
        Visualize genetic relationships and identify population subgroups.
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
        </div>
      )}

      {/* Table Selection */}
      <div style={styles.section}>
        <label style={styles.label}>
          <strong>Select Genotype Table:</strong>
        </label>
        <select
          value={selectedTable}
          onChange={(e) => setSelectedTable(e.target.value)}
          style={styles.select}
          disabled={loading}
        >
          <option value="">-- Select a table --</option>
          {tables.map(table => (
            <option key={table} value={table}>
              {table}
            </option>
          ))}
        </select>
      </div>

      {selectedTable && (
        <>
          {/* Row Type Filter */}
          <div style={styles.section}>
            <label style={styles.label}>
              <strong>🌾 Row Type Filter (Barley):</strong>
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
              <button
                onClick={() => setRadFilter(radFilter === '2' ? null : '2')}
                style={{
                  padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  border: radFilter === '2' ? '2px solid #2e86de' : '1px solid #adb5bd',
                  backgroundColor: radFilter === '2' ? '#2e86de' : '#fff',
                  color: radFilter === '2' ? '#fff' : '#495057',
                }}
              >
                2-Row
              </button>
              <button
                onClick={() => setRadFilter(radFilter === '6' ? null : '6')}
                style={{
                  padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  border: radFilter === '6' ? '2px solid #e67e22' : '1px solid #adb5bd',
                  backgroundColor: radFilter === '6' ? '#e67e22' : '#fff',
                  color: radFilter === '6' ? '#fff' : '#495057',
                }}
              >
                6-Row
              </button>
              <span style={{ fontSize: '13px', color: '#888', marginLeft: '8px' }}>
                {radFilter ? `Analyzing only ${radFilter}-row barley` : 'All samples (no filter)'}
              </span>
            </div>
          </div>

          {/* PCA Parameters */}
          <div style={styles.parametersSection}>
            <h3 style={styles.sectionHeading}>PCA Parameters</h3>
            <div style={styles.parameterGrid}>
              <div style={styles.parameterItem}>
                <label style={styles.paramLabel}>Number of Components:</label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={nComponents}
                  onChange={(e) => setNComponents(parseInt(e.target.value))}
                  style={styles.numberInput}
                  title="Number of principal components to compute (2-10)"
                />
              </div>
              <div style={styles.parameterItem}>
                <label style={styles.paramLabel}>
                  Min MAF: 
                  <span style={styles.helpText}> (Minor Allele Frequency, 0.01 recommended)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="0.5"
                  value={minMaf}
                  onChange={(e) => setMinMaf(parseFloat(e.target.value))}
                  style={styles.numberInput}
                  title="Minimum minor allele frequency - lower values keep more markers"
                />
              </div>
              <div style={styles.parameterItem}>
                <label style={styles.paramLabel}>
                  Max Missing: 
                  <span style={styles.helpText}> (0.5 = 50% missing allowed)</span>
                </label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={maxMissing}
                  onChange={(e) => setMaxMissing(parseFloat(e.target.value))}
                  style={styles.numberInput}
                  title="Maximum proportion of missing data per marker - higher values keep more markers"
                />
              </div>
            </div>
            <button
              onClick={handleRunPCA}
              disabled={pcaLoading}
              style={styles.runButton}
            >
              {pcaLoading ? '⏳ Running PCA...' : '🔬 Run PCA Analysis'}
            </button>
          </div>

          {/* Clustering Parameters */}
          {pcaResults && (
            <div style={styles.parametersSection}>
              <h3 style={styles.sectionHeading}>Clustering Analysis</h3>
              <div style={styles.parameterGrid}>
                <div style={styles.parameterItem}>
                  <label style={styles.paramLabel}>Number of Clusters:</label>
                  <input
                    type="number"
                    min="2"
                    max="10"
                    value={nClusters}
                    onChange={(e) => setNClusters(parseInt(e.target.value))}
                    style={styles.numberInput}
                  />
                </div>
                <div style={styles.parameterItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={usePca}
                      onChange={(e) => setUsePca(e.target.checked)}
                      style={styles.checkbox}
                    />
                    Use PCA components for clustering
                  </label>
                </div>
              </div>
              <button
                onClick={handleRunClustering}
                disabled={clusterLoading}
                style={styles.runButton}
              >
                {clusterLoading ? '⏳ Running Clustering...' : '🎯 Run K-means Clustering'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Results */}
      {pcaResults && (
        <div style={styles.resultsSection}>
          <div style={styles.resultsHeader}>
            <h3 style={styles.resultsHeading}>Analysis Results</h3>
            <button onClick={downloadPCAData} style={styles.downloadButton}>
              📥 Download PCA Data
            </button>
            {clusterResults && (
              <button onClick={downloadClusterData} style={styles.downloadButton}>
                📥 Download Cluster Data
              </button>
            )}
          </div>

          <div style={styles.resultsSummary}>
            <div>
              <strong>Samples:</strong> {pcaResults.n_samples}
            </div>
            <div>
              <strong>Markers Used:</strong> {pcaResults.n_markers.toLocaleString()}
            </div>
            <div>
              <strong>Components:</strong> {pcaResults.n_components}
            </div>
            {clusterResults && (
              <div>
                <strong>Clusters:</strong> {clusterResults.n_clusters}
              </div>
            )}
          </div>

          {renderPCAPlot()}
          {renderVarianceChart()}

          {/* Top Loadings */}
          {pcaResults.loadings && pcaResults.loadings.length > 0 && (
            <div style={styles.loadingsSection}>
              <h4>Top Contributing Markers</h4>
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>PC</th>
                      <th style={styles.th}>Marker</th>
                      <th style={styles.th}>Loading</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pcaResults.loadings.slice(0, 15).map((item, idx) => (
                      <tr key={idx} style={styles.tr}>
                        <td style={styles.td}>{item.pc}</td>
                        <td style={styles.td}>{item.marker}</td>
                        <td style={styles.td}>{item.loading.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '30px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  heading: {
    color: '#025c4f',
    fontSize: '28px',
    marginBottom: '20px',
    borderBottom: '3px solid #025c4f',
    paddingBottom: '10px',
  },
  infoBox: {
    backgroundColor: '#dbf8f4',
    border: '1px solid #8bcec0',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '25px',
    color: '#025c4f',
    lineHeight: '1.6',
  },
  errorBox: {
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '20px',
    color: '#c33',
  },
  section: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '16px',
    color: '#333',
  },
  select: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  parametersSection: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '25px',
  },
  sectionHeading: {
    color: '#025c4f',
    fontSize: '20px',
    marginBottom: '15px',
  },
  parameterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  parameterItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  paramLabel: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
  helpText: {
    fontSize: '12px',
    color: '#888',
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  numberInput: {
    padding: '10px',
    fontSize: '16px',
    border: '2px solid #ddd',
    borderRadius: '6px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#555',
  },
  checkbox: {
    marginRight: '8px',
    width: '18px',
    height: '18px',
  },
  runButton: {
    backgroundColor: '#025c4f',
    color: 'white',
    border: 'none',
    padding: '14px 30px',
    fontSize: '16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    transition: 'background-color 0.3s',
  },
  resultsSection: {
    marginTop: '40px',
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  resultsHeading: {
    color: '#025c4f',
    fontSize: '24px',
    margin: 0,
  },
  downloadButton: {
    backgroundColor: '#2d7a6e',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    fontSize: '14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  resultsSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #ddd',
    marginBottom: '30px',
  },
  plotContainer: {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '25px',
    marginBottom: '30px',
  },
  plotHeader: {
    marginBottom: '20px',
  },
  axisSelectors: {
    marginTop: '15px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  axisSelect: {
    marginLeft: '10px',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  svg: {
    display: 'block',
    margin: '0 auto',
  },
  axisLabel: {
    fontSize: '14px',
    fill: '#333',
    fontWeight: 'bold',
  },
  legend: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    border: '1px solid #ddd',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
  },
  legendColor: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    marginRight: '10px',
    border: '1px solid #fff',
  },
  varianceChart: {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '25px',
    marginBottom: '30px',
  },
  varianceBar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '15px',
  },
  varianceLabel: {
    width: '60px',
    fontWeight: 'bold',
    color: '#025c4f',
  },
  varianceBarContainer: {
    flex: 1,
    height: '30px',
    backgroundColor: '#eee',
    borderRadius: '15px',
    overflow: 'hidden',
  },
  varianceBarFill: {
    height: '100%',
    backgroundColor: '#025c4f',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '10px',
    transition: 'width 0.5s ease',
  },
  varianceText: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  cumulativeVariance: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#dbf8f4',
    borderRadius: '6px',
    color: '#025c4f',
  },
  loadingsSection: {
    marginTop: '30px',
  },
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #ddd',
    marginTop: '15px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '15px',
  },
  th: {
    backgroundColor: '#025c4f',
    color: 'white',
    padding: '14px',
    textAlign: 'left',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  tr: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '14px',
    textAlign: 'left',
  },
};

export default PopulationStructure;
